import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Play, Variable, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FormulaSimulator = ({ formData }) => {
  const [variables, setVariables] = useState({});
  const [quantityManual, setQuantityManual] = useState('1');
  const [result, setResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [allVarNames, setAllVarNames] = useState([]);
  const debounceRef = useRef(null);

  // Extract all variable names from all formulas
  useEffect(() => {
    const formulas = [
      formData.unit_price_formula,
      formData.quantity_formula,
      formData.total_formula,
    ].filter(Boolean);

    if (formulas.length === 0 && formData.unit_price_type !== 'formula' && formData.quantity_type !== 'formula') {
      setAllVarNames([]);
      return;
    }

    const extractVars = async () => {
      const names = new Set();
      for (const f of formulas) {
        try {
          const res = await axios.post(`${API_URL}/formula/validate`, { formula: f }, { withCredentials: true });
          if (res.data.valid && res.data.variables) {
            res.data.variables.forEach(v => names.add(v));
          }
        } catch {}
      }
      const sorted = Array.from(names).sort();
      setAllVarNames(sorted);
      // Initialize missing variables
      setVariables(prev => {
        const updated = { ...prev };
        sorted.forEach(v => { if (updated[v] === undefined) updated[v] = ''; });
        return updated;
      });
    };
    extractVars();
  }, [formData.unit_price_formula, formData.quantity_formula, formData.total_formula, formData.unit_price_type, formData.quantity_type]);

  // Auto-simulate on variable change (debounced)
  const simulate = useCallback(async () => {
    const hasFormula = (formData.unit_price_type === 'formula' && formData.unit_price_formula) ||
                       (formData.quantity_type === 'formula' && formData.quantity_formula) ||
                       formData.total_formula;
    const hasFixed = formData.unit_price_type === 'fixed' && formData.unit_price_fixed;

    if (!hasFormula && !hasFixed) {
      setResult(null);
      return;
    }

    setSimulating(true);
    try {
      const res = await axios.post(`${API_URL}/formula/simulate`, {
        unit_price_type: formData.unit_price_type,
        unit_price_fixed: formData.unit_price_fixed ? parseFloat(formData.unit_price_fixed) : null,
        unit_price_formula: formData.unit_price_formula || null,
        quantity_type: formData.quantity_type,
        quantity_formula: formData.quantity_formula || null,
        quantity_manual: parseFloat(quantityManual) || 1,
        total_formula: formData.total_formula || null,
        variables,
      }, { withCredentials: true });
      setResult(res.data);
    } catch (error) {
      setResult({ errors: [error.response?.data?.detail || 'Erro na simulacao'] });
    } finally {
      setSimulating(false);
    }
  }, [formData, variables, quantityManual]);

  // Debounce auto-simulation
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => simulate(), 600);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [simulate]);

  const hasAnyConfig = formData.unit_price_type === 'formula' || formData.quantity_type === 'formula' ||
                        formData.total_formula || (formData.unit_price_type === 'fixed' && formData.unit_price_fixed);

  if (!hasAnyConfig) return null;

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
  };

  const formatNumber = (val) => {
    if (val == null) return '-';
    return Number(val).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  };

  return (
    <div className="border-2 border-dashed border-[#AF2628]/30 rounded-lg p-4 bg-[#FFF8F8]" data-testid="formula-simulator">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold text-[#AF2628] uppercase tracking-wide flex items-center gap-1.5">
          <Play className="w-3.5 h-3.5" />
          Simulador de Formula
        </h4>
        {simulating && <span className="text-[10px] text-[#AEAEB2] animate-pulse">Calculando...</span>}
      </div>

      <div className="space-y-3">
        {/* Variable inputs */}
        {allVarNames.length > 0 && (
          <div>
            <p className="text-[10px] text-[#6E6E73] mb-1.5 font-medium">Informe valores de teste para as variaveis:</p>
            <div className="grid grid-cols-2 gap-2">
              {allVarNames.map(varName => (
                <div key={varName} className="flex items-center gap-2">
                  <Label className="text-[10px] font-mono text-[#6E6E73] w-28 flex-shrink-0 truncate" title={varName}>{varName}</Label>
                  <Input
                    type="number" step="any"
                    value={variables[varName] || ''}
                    onChange={(e) => setVariables(prev => ({ ...prev, [varName]: e.target.value }))}
                    className="h-7 text-xs"
                    placeholder="0"
                    data-testid={`sim-var-${varName}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual quantity input */}
        {formData.quantity_type === 'manual' && (
          <div className="flex items-center gap-2">
            <Label className="text-[10px] font-mono text-[#6E6E73] w-28 flex-shrink-0">QUANTIDADE</Label>
            <Input
              type="number" step="any" min="0"
              value={quantityManual}
              onChange={(e) => setQuantityManual(e.target.value)}
              className="h-7 text-xs"
              data-testid="sim-quantity-manual"
            />
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="mt-2 pt-2 border-t border-[#AF2628]/20">
            {result.errors && result.errors.length > 0 && (
              <div className="flex items-start gap-1.5 mb-2 text-red-600">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div className="text-[11px]">{result.errors.join('; ')}</div>
              </div>
            )}
            {(!result.errors || result.errors.length === 0) && (
              <div className="flex items-center gap-1.5 mb-2 text-green-600">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">Calculo valido</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-lg px-3 py-2 border border-[#E5E5EA]">
                <span className="text-[9px] text-[#AEAEB2] block uppercase">Quantidade</span>
                <span className="text-sm font-bold text-[#111111]" data-testid="sim-result-qty">{formatNumber(result.quantity)}</span>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-[#E5E5EA]">
                <span className="text-[9px] text-[#AEAEB2] block uppercase">Vlr. Unitario</span>
                <span className="text-sm font-bold text-[#111111]" data-testid="sim-result-price">{formatCurrency(result.unit_price)}</span>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-[#AF2628]/30">
                <span className="text-[9px] text-[#AF2628] block uppercase font-semibold">Vlr. Total</span>
                <span className="text-sm font-bold text-[#AF2628]" data-testid="sim-result-total">{formatCurrency(result.total)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormulaSimulator;
